import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CalculatorProfitMargin = () => {
  const [revenue, setRevenue] = useState('');
  const [costOfGoodsSold, setCostOfGoodsSold] = useState('');
  const [operatingExpenses, setOperatingExpenses] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleRevenueChange = (e) => {
    setRevenue(e.target.value);
    setError('');
  };

  const handleCostOfGoodsSoldChange = (e) => {
    setCostOfGoodsSold(e.target.value);
    setError('');
  };

  const handleOperatingExpensesChange = (e) => {
    setOperatingExpenses(e.target.value);
    setError('');
  };

  const calculateProfitMargin = () => {
    if (!revenue || !costOfGoodsSold || !operatingExpenses) {
      setError('Please enter all required values.');
      return;
    }

    const rev = parseFloat(revenue);
    const cogs = parseFloat(costOfGoodsSold);
    const expenses = parseFloat(operatingExpenses);

    if (isNaN(rev) || isNaN(cogs) || isNaN(expenses) || rev <= 0) {
      setError('Please enter valid numbers and ensure revenue is positive.');
      return;
    }

    // Calculate gross profit
    const grossProfit = rev - cogs;

    // Calculate operating profit
    const operatingProfit = grossProfit - expenses;

    // Calculate profit margin
    const profitMargin = (operatingProfit / rev) * 100;

    setResult(profitMargin);

    // Update chart data
    setChartData({
      labels: ['Revenue', 'Cost of Goods Sold', 'Operating Expenses', 'Operating Profit', 'Profit Margin'],
      datasets: [
        {
          label: 'Profit Margin Analysis',
          data: [rev, cogs, expenses, operatingProfit, profitMargin],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(153, 102, 255, 1)', 'rgba(255, 159, 64, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Revenue: ${rev.toFixed(2)}`,
      `Cost of Goods Sold: ${cogs.toFixed(2)}`,
      `Operating Expenses: ${expenses.toFixed(2)}`,
      `Gross Profit: Revenue - Cost of Goods Sold = ${grossProfit.toFixed(2)}`,
      `Operating Profit: Gross Profit - Operating Expenses = ${operatingProfit.toFixed(2)}`,
      `Profit Margin: (Operating Profit / Revenue) * 100 = ${profitMargin.toFixed(2)}%`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateProfitMargin();
  };

  return (
    <>
      <Helmet>
        <title>Profit Margin Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate your profit margin with ease. Understand your business's profitability using this calculator." />
        <meta name="keywords" content="profit margin calculator, business calculator, financial calculator, margin calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">📊</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Profit Margin Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate your profit margin and understand your business's profitability with ease.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Revenue</label>
                <input
                  type="number"
                  value={revenue}
                  onChange={handleRevenueChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter total revenue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cost of Goods Sold (COGS)</label>
                <input
                  type="number"
                  value={costOfGoodsSold}
                  onChange={handleCostOfGoodsSoldChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter cost of goods sold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Operating Expenses</label>
                <input
                  type="number"
                  value={operatingExpenses}
                  onChange={handleOperatingExpensesChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter operating expenses"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate Profit Margin
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Profit Margin: {result.toFixed(2)}%</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Profit Margin Analysis' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is profit margin?</h3>
                <p className="text-gray-600">
                  Profit margin is a financial metric that measures the profitability of a business by comparing operating profit to total revenue. It's expressed as a percentage.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How is profit margin calculated?</h3>
                <p className="text-gray-600">
                  Profit margin is calculated using the formula: (Operating Profit / Revenue) * 100. Operating profit is determined by subtracting cost of goods sold and operating expenses from revenue.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is cost of goods sold (COGS)?</h3>
                <p className="text-gray-600">
                  Cost of goods sold (COGS) represents the direct costs associated with producing goods or services, including raw materials and direct labor.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What are operating expenses?</h3>
                <p className="text-gray-600">
                  Operating expenses include all costs associated with running a business, such as rent, salaries, utilities, and marketing.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using standard mathematical formulas. Results are rounded to two decimal places for readability.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Profit Margin Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Revenue</h3>
                <p className="text-gray-600">
                  Input your total revenue from sales or services.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Cost of Goods Sold</h3>
                <p className="text-gray-600">
                  Input the total cost of producing your goods or services.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Enter Operating Expenses</h3>
                <p className="text-gray-600">
                  Input your operating expenses, including rent, salaries, and other business costs.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate Profit Margin" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed as a percentage, along with a bar chart and solution steps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalculatorProfitMargin;